import React, { useEffect, useRef, useState } from "react";
import { Form, FormElement, FormRenderProps } from "@progress/kendo-react-form";
import styles from "styles.module.css";
import CustomButton from "ui/Button";
import { COLOR, ICON_NAMES } from "utils/constant";
import CustomStepper from "ui/CustomStepper";
import FormStepOne from "./form/StepOne";
import FormStepThree from "./form/StepThree";
import {
	getSalesNoteHeaderApi,
	completeSalesNoteApi,
	saveSalesNoteHead,
	newSalesNoteApi,
	setNoteHeadStepApi,
} from "api/salesNote";
import { useNavigate, useParams } from "react-router";
import { SalesNoteHead, SalesNoteLine } from "interface/salesNote";
import FormStepTwo from "./form/StepTwo";
import Signature from "./form/Signature";
import PdfViewer from "./form/PdfViewer";
import { sendToSign } from "api/sign";
import { PageTypePath } from "utils/pages";
import { useInfoStore } from "store/error";
import { usePageHeight } from "hooks/hooks";
import { t } from "i18next";
import { useToastStore } from "store/toast";
import Info from "ui/Info";
import { set } from "lodash";
import { stepTwoRequirementCheck } from "./utils";
import { useUserStore } from "store/user";
import CustomModal from "ui/modal/Modal";

interface LineForm extends SalesNoteLine {
	id: number;
	inEdit: boolean;
}

interface SignatoresForm {
	vesselSigner: string;
	apprenticeSigner: string;
	buyerSigner: string;
	quotaTypeCode: string;
	showError: boolean;
}

function SalesNoteForm() {
	const { id, action } = useParams();
	const [step, setStep] = useState(0);
	const [form, setForm] = useState<SalesNoteHead>(Object());
	const [lineForm, setLineForm] = useState<LineForm[]>([]);
	const [signers, setSigners] = useState<SignatoresForm>(Object);
	const [allRequirementsMet, setAllRequirementsMet] = useState(false);
	const [showRequirementMetError, setShowRequirementMetError] = useState(false);
	const navigate = useNavigate();
	const { setError, setInfo, resetStore } = useInfoStore();
	const { showError } = useToastStore();
	const pageHeight = usePageHeight();
	const [isMobileView, setIsMobileView] = useState(
		() => window.innerWidth < 768,
	);
	const [completedDocumentNumber, setCompletedDocumentNumber] = useState<
		string | null
	>(null);

	const [formSaved, setFormSaved] = useState(true);
	const { isAdmin } = useUserStore();

	const [showCompletionModal, setShowCompletionModal] = useState(false);
	const [completionModalContent, setCompletionModalContent] = useState("");

	const maxStep = 4;

	const stepsItems = [
		{ label: t("CatchInformation"), optional: false },
		{ label: t("ProductLines"), optional: false },
		{ label: t("NoteLines"), optional: true },
		{ label: t("PreviewAndComplete"), optional: true },
		{ label: t("Sign"), optional: true, disabled: true },
	];

	const onSubmit = () => {};

	const handleDelete = () => {};

	const handleFinish = () => {
		completeSalesNoteApi(id as any)
			.then((data) => {
				if (data.returnCode === 100) {
					// Handle validation errors - convert object to string

					const errorMessage = typeof data.data === 'string' 
						? data.data 
						: JSON.stringify(data.data);
					setCompletionModalContent(errorMessage);
					setShowCompletionModal(true);
					return;
				}

				if (data.data !== "") {
					setCompletedDocumentNumber(data.data);
					navigate(`/${PageTypePath.Signature}/${data.data}`, {
						state: {
							quotaTypeCode: form.quotaTypeCode,
							noteType: 0, // for sales note 1 for landing note
						},
					});
				} else {
					alert("Kunne ikke fullføre sluttseddel");
				}
			})
			.catch((error) => {
				console.log(error);
			});
	};

	const handleCompletionModalConfirm = () => {
		setShowCompletionModal(false);
		setCompletionModalContent("");

	//	navigate(`/${PageTypePath.Signature}/${completedDocumentNumber}`, {
	//		state: {
	//			quotaTypeCode: form.quotaTypeCode,
	//			noteType: 0, // for sales note 1 for landing note
	//		},
	//	});
	};

	const handleNext = () => {
		if (lineForm.length === 0 && step === 1) {
			showError(t("AtLeastOneLineRequired"), {
				duration: 5000,
				position: "bottom-center",
			});
			return;
		}

		if (!allRequirementsMet) {
			setShowRequirementMetError(true);
			return;
		}

		let nextStep = step + 1;

		setStep(nextStep);

		if (form.currentStep <= nextStep) {
			setNoteHeadStepApi(form.id, nextStep)
				.then((response) => {})
				.catch((err) => {
					console.log(err);
					if (err?.response?.data) {
						setError(err?.response?.data.returnText);
					} else {
						setError(err.message);
					}
				});
			form.currentStep = nextStep;
		}
		navigate(`/${PageTypePath.SalesNoteForm}/${id}/${nextStep}`);

		setShowRequirementMetError(false);
	};

	const handlePrevoius = () => {
		if (lineForm.length === 0 && step === 1) {
			showError(t("AtLeastOneLineRequired"), {
				duration: 5000,
				position: "bottom-center",
			});
			return;
		}

		let nextStep = step - 1;

		setStep(nextStep);
		if (form.currentStep >= nextStep) {
			setNoteHeadStepApi(form.id, nextStep)
				.then((response) => {})
				.catch((err) => {
					console.log(err);
					if (err?.response?.data) {
						setError(err?.response?.data.returnText);
					} else {
						setError(err.message);
					}
				});
			form.currentStep = nextStep;
		}
		navigate(`/${PageTypePath.SalesNoteForm}/${id}/${nextStep}`);

		setShowRequirementMetError(false);
	};

	useEffect(() => {
		const handleResize = () => {
			setIsMobileView(window.innerWidth < 768);
		};

		window.addEventListener("resize", handleResize);
		return () => {
			window.removeEventListener("resize", handleResize);
		};
	}, []);

	useEffect(() => {
		if (!id) {
			return;
		}
		getSalesNoteHeaderApi(id as any)
			.then((data) => {
				setForm(data.data);
				setSigners({
					...signers,
					quotaTypeCode: data.data.quotaTypeCode as any,
				});

				const requestedStep = parseInt(action ?? "0");
				const maxAllowedStep = data.data.currentStep ?? 0;
				const validatedStep =
					!Number.isNaN(requestedStep) && requestedStep <= maxAllowedStep
						? requestedStep
						: maxAllowedStep;

				setStep(validatedStep);
				navigate(`/${PageTypePath.SalesNoteForm}/${id}/${validatedStep}`, {
					replace: true,
				});
			})
			.catch((error) => {
				console.log(error);
			});
	}, []);

	useEffect(() => {
		if (signers.showError) {
			if (
				signers.buyerSigner &&
				signers.buyerSigner.trim() !== "" &&
				signers.buyerSigner !== undefined &&
				signers.buyerSigner !== null &&
				signers.vesselSigner &&
				signers.vesselSigner.trim() !== "" &&
				signers.vesselSigner !== undefined &&
				signers.vesselSigner !== null
			) {
				setSigners({ ...signers, showError: false });
			}
		}
	}, [signers]);

	const sendSigners = () => {
		if (
			!signers.buyerSigner ||
			signers.buyerSigner.trim() === "" ||
			!signers.vesselSigner ||
			signers.vesselSigner.trim() === ""
		) {
			setSigners({ ...signers, showError: true });
			return;
		}
		sendToSign({
			...signers,
			documentNumber: completedDocumentNumber as any, //form.noteNumber as any
		})
			.then((data) => {
				//navigate("/sluttsedler");
				navigate(`/${PageTypePath.Signature}/${form.noteNumber}`, {
					state: {
						quotaTypeCode: form.quotaTypeCode,
						noteType: 0, // for sales note 1 for landing note
					},
				});
			})
			.catch((err) => {
				if (err?.response?.data) {
					setError(err?.response?.data.returnText);
				} else {
					setError(err.message);
				}
			});
	};

	const handleStepClick = (_step: number) => {
		if (_step === maxStep) {
			return;
		}

		//allow to go back without validation
		if (_step < step) {
			setStep(_step);
			navigate(`/${PageTypePath.SalesNoteForm}/${id}/${_step}`);
			return;
		}
		if (!stepTwoRequirementCheck(lineForm, isAdmin) && step === 1) {
			showError(t("AtLeastOneLineRequired"), {
				duration: 5000,
				position: "bottom-center",
			});
			return;
		}
		if (!allRequirementsMet && _step > step) {
			setShowRequirementMetError(true);
			return;
		}
		if (_step > step + 1) {
			if (!stepTwoRequirementCheck(lineForm, isAdmin)) {
				showError(t("AtLeastOneLineRequired"), {
					duration: 5000,
					position: "bottom-center",
				});
				setStep(step + 1);
				return;
			}
		}
		setStep(_step);
		navigate(`/${PageTypePath.SalesNoteForm}/${id}/${_step}`);
	};
	const handleMoreClick = () => {
		newSalesNoteApi(form.id as any)
			.then((response) => {
				setInfo({
					show: true,
					title: t("NewNote"),
					description: t("NewNoteCreated"),
					infoType: "Info",
					actions: [
						{
							text: t("Ok"),
							btn: () => {
								resetStore();
							},
						},
					],
				});
			})
			.catch((error) => {
				console.log(error);
			});
	};

	const addLineRef = useRef<(() => void) | null>(null);

	return (
		<div
			className={`${styles.PageContainer} sales-note-page`}
			style={{ height: "100%" }}
		>
			{showCompletionModal && (
				<CustomModal
					title={t("NoteCompletion")}
					onClose={() => setShowCompletionModal(false)}
					actions={[
						{
							label: t("Ok"),
							onClick: handleCompletionModalConfirm,
							bgColor: COLOR.StatusRed,
							color: "white",
						},
					]}
				>
					<p style={{ padding: "16px 0" }} dangerouslySetInnerHTML={{ __html: completionModalContent }} />
				</CustomModal>




			)}
			<div
				style={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
				}}
			>
				<legend
					className={styles.BoxHeadingLarge}
					style={{ paddingBottom: "20px" }}
				>
					{t("EditSalesNote")}
				</legend>
				{step < 4 && (
					<CustomButton
						onClick={handleMoreClick}
						label={t("More")}
						title={t("NewNoteThisLanding")}
						iconName={ICON_NAMES.Plus}
						btnStyle={isMobileView ? { width: "100%" } : undefined}
					/>
				)}
			</div>
			<CustomStepper
				steps={stepsItems}
				activeStep={step}
				setActiveStep={handleStepClick}
			/>
			<div
				style={{
					display: "flex",
					flexDirection: "column",
					height: isMobileView ? "auto" : "100%",
					gap: isMobileView ? "16px" : "0px",
					justifyContent: "space-between",
				}}
			>
				<div style={{}}>
					<Form
						onSubmit={onSubmit}
						render={(formRenderProps: FormRenderProps) => (
							<FormElement
								className="sales-note-form-element"
								style={{
									width: "100%",
									height: isMobileView
										? "auto"
										: `calc(${pageHeight}px - 400px)`,
									minHeight: isMobileView ? "calc(100dvh - 320px)" : undefined,
								}}
							>
								<fieldset className={"k-form-fieldset"}>
									{step === 0 && (
										<FormStepOne
											form={form}
											setForm={setForm}
											setAllRequirementsMet={setAllRequirementsMet}
											showRequirementMetError={showRequirementMetError}
											setFormSaved={setFormSaved}
										/>
									)}
									{step === 1 && (
										<FormStepTwo
											setForm={setLineForm}
											form={lineForm}
											showRequirement={showRequirementMetError}
											setMeetRequirement={setAllRequirementsMet}
											addLineRef={addLineRef}
										/>
									)}
									{step === 2 && <FormStepThree noteHeaderId={id as any} />}
									{step === 3 && (
										<PdfViewer reportGuid={form.reportGuid as string} />
									)}
									{step === maxStep && (
										<Signature setSigners={setSigners} signers={signers} />
									)}
								</fieldset>
							</FormElement>
						)}
					/>
				</div>

				<div>
					<div
						className="sales-note-actions-row"
						style={{
							display: "flex",
							flexDirection: isMobileView ? "column" : "row",
							justifyContent: "space-between",
							gap: isMobileView ? "12px" : "0px",
							flex: 1,
						}}
					>
						<div
							className="sales-note-actions-left"
							style={{ display: "flex", gap: "10px", alignItems: "center" }}
						>
							<div
								className="sales-note-saved-indicator"
								style={{ display: "flex", gap: "10px" }}
							>
								<span
									style={{ paddingTop: "7px" }}
									className={styles.TableText}
								>
									{t("AllChangesSaved")}
								</span>
								<span style={{ paddingTop: "7px" }}>
									<img
										src={`/icon/${
											formSaved ? "checkmark_green" : ICON_NAMES.Checkbox
										}.svg`}
										alt={"saved icon"}
									/>
								</span>
							</div>
							{step === 1 && (
								<CustomButton
									label={t("NewLine") + " +"}
									onClick={() => addLineRef.current?.()}
									bgColor={COLOR.Green}
									color="white"
									btnStyle={isMobileView ? { width: "100%" } : undefined}
								/>
							)}
						</div>
						<div
							className="sales-note-actions-right"
							style={{ display: "flex", gap: "10px" }}
						>
							<CustomButton
								onClick={handlePrevoius}
								label={t("Previous")}
								iconName={ICON_NAMES.Prevous}
								iconRight={false}
								disabled={step === 0}
								btnStyle={isMobileView ? { width: "100%" } : undefined}
							/>
							{step === 3 && (
								<>
									<CustomButton
										onClick={handleFinish}
										label={t("Complete")}
										bgColor={COLOR.Green}
										color={"white"}
										iconName={ICON_NAMES.FinishTransparent}
										btnStyle={isMobileView ? { width: "100%" } : undefined}
									/>
								</>
							)}

							{step < 3 && (
								<CustomButton
									onClick={handleNext}
									label={t("Next")}
									bgColor={COLOR.Green}
									color={"white"}
									iconName={ICON_NAMES.NextWhite}
									btnStyle={isMobileView ? { width: "100%" } : undefined}
								/>
							)}
							{/*{step === maxStep && (*/}
							{/*	<CustomButton*/}
							{/*		onClick={() =>*/}
							{/*			setSigners({*/}
							{/*				...Object(),*/}
							{/*				quotaTypeCode: form.quotaTypeCode,*/}
							{/*			})*/}
							{/*		}*/}
							{/*		label={"Ny signant +"}*/}
							{/*		// bgColor={COLOR.Green}*/}
							{/*		color={"black"}*/}
							{/*		// iconName={ICON_NAMES.NextWhite}*/}
							{/*	/>*/}
							{/*)}*/}
							{step === maxStep && (
								<CustomButton
									onClick={sendSigners}
									label={t("SendToSigning")}
									bgColor={COLOR.Green}
									color={"white"}
									iconName={ICON_NAMES.CheckMark}
									btnStyle={isMobileView ? { width: "100%" } : undefined}
								/>
							)}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

export default SalesNoteForm;
